import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";
import { errorMessage } from "../../utility";

const TABS = [
  { key: "file", label: "Upload File" },
  { key: "link", label: "Paste Link" },
  { key: "text", label: "Type Answer" },
];

const ACCEPTED_FILES =
  ".pdf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.json,.ipynb,.py,.jpg,.jpeg,.png,.gif,.webp,.mp4";

const HONEYPOT_STYLE = {
  position: "absolute",
  left: "-9999px",
  width: 1,
  height: 1,
  opacity: 0,
  overflow: "hidden",
};

const UploadAnswers = ({ id, onClose }) => {
  const [method, setMethod] = useState("file");
  const [loading, setLoading] = useState(false);

  // Bot guard — hidden honeypot + how long the form has been open.
  const startedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  // Upload File
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Paste Link
  const [linkValue, setLinkValue] = useState("");

  // Type Answer
  const [answerText, setAnswerText] = useState("");

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setFile(selected);
  };

  const canSubmit =
    (method === "file" && !!file) ||
    (method === "link" && linkValue.trim().length > 0) ||
    (method === "text" && answerText.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setUploadProgress(0);
    try {
      if (method === "file") {
        const fd = new FormData();
        fd.append("exercise_id", id);
        fd.append("file", file);
        fd.append("website", honeypot);
        fd.append("form_started_at", String(startedAt.current));
        await client.post(`courses/live/live-courses/assignments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
      } else {
        const payload = {
          exercise_id: id,
          website: honeypot,
          form_started_at: startedAt.current,
        };
        if (method === "link") payload.file_link = linkValue.trim();
        if (method === "text") payload.answer_text = answerText.trim();
        await client.post(`courses/live/live-courses/assignments`, payload);
      }
      toast.success("Assignment submitted successfully");
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="innersection" display="flex" flexDirection="column" pl={{ sm: 2, md: 5 }} pr={{ sm: 2, md: 5 }} marginBottom={8}>
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={HONEYPOT_STYLE}
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
      <h1 style={{ marginTop: 40, fontWeight: 600, marginBottom: 10 }}>Submit Your Answer</h1>

      {/* Method tabs */}
      <Box display="flex" gap={1} mb={3}>
        {TABS.map((t) => (
          <Button
            key={t.key}
            onClick={() => setMethod(t.key)}
            variant={method === t.key ? "contained" : "outlined"}
            size="small"
            style={
              method === t.key
                ? { background: "#d15242", boxShadow: "none" }
                : { borderColor: "#d15242", color: "#d15242" }
            }
          >
            {t.label}
          </Button>
        ))}
      </Box>

      <Box bgcolor="#f7f7f7" p={4} borderRadius={2} width="100%">
        {method === "file" && (
          <>
            <Box
              p={4}
              border="2px dashed"
              borderColor={isDragging ? "primary.main" : "grey.400"}
              borderRadius={2}
              textAlign="center"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <h1 style={{ marginBottom: 20, fontSize: 25, fontWeight: 600 }}>Drag & Drop a File</h1>
              <Button
                variant="contained"
                component="label"
                color="warning"
                style={{ background: "#D15242", boxShadow: "none" }}
              >
                Browse Files
                <input type="file" hidden accept={ACCEPTED_FILES} onChange={handleFileChange} />
              </Button>
              <p style={{ marginTop: 20 }}>
                One file per assignment, max 10 MB. Notebooks (.ipynb) and Python (.py) files are accepted.
              </p>
              {file && (
                <Typography variant="body2" color="textSecondary" mt={2}>
                  Selected File: {file.name}
                </Typography>
              )}
            </Box>

            {loading && (
              <Box mt={3}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="body2" mt={2}>
                  Uploading: {uploadProgress}%
                </Typography>
              </Box>
            )}
          </>
        )}

        {method === "link" && (
          <Box>
            <Typography variant="body2" mb={1.5}>
              Paste a link to your work (Google Drive, GitHub, Figma, etc.)
            </Typography>
            <input
              type="url"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="https://..."
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid #ccc",
                fontSize: 14,
                boxSizing: "border-box",
              }}
            />
          </Box>
        )}

        {method === "text" && (
          <Box>
            <Typography variant="body2" mb={1.5}>
              Type your answer below
            </Typography>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Write your answer here..."
              rows={8}
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid #ccc",
                fontSize: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          </Box>
        )}
      </Box>

      <Button
        onClick={handleSubmit}
        disabled={!canSubmit || loading}
        size="large"
        variant="contained"
        style={{ background: "#d15242", minHeight: 50, marginTop: 30 }}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>
    </Box>
  );
};

export default UploadAnswers;
